﻿import { useState, useMemo, useCallback } from "react";
import { useToasts } from "react-toast-notifications";
import { useNavigate } from "react-router-dom";
import { Modal } from "react-bootstrap";
import { useQueryClient } from "react-query";
import { Eye, FileText, Paperclip, Copy, Edit, CheckCircle, XCircle, Undo2, Search, Trash2, CircleStop, Bell } from "lucide-react";

import { hasPermission, validRoles } from "@/services/Utils";
import Fetching from "@/components/Fetching/Fetching";
import BasicConfirm from "@/components/Confirm/BasicConfirm";
import CustomDropdown from "@/components/desktop/list/dropdown/CustomDropdown";

import {
	useAppOrderApprove,
	useAppOrderDelete,
	useAppOrderReject,
	useAppOrderFinish,
	useApplicationOrdersPaginate,
	useAppOrderDuplicate,
	useAppOrderReturnState,
	useAppOrderCheckStatus,
} from "@/hooks/planning/application-order/applicationOrders";
import { useDownloadPdf } from "@/hooks/planning/application-order/appOrdersDocs";

import classes from "./orderApplicationAction.module.scss";

const AppOrdersActionItem = ({ item, indice, setData, base, title = "Acciones", approveRejectOnly = false, approveRejectOnlyComponent = <></> }) => {
	const queryClient = useQueryClient();
	const { addToast } = useToasts();

	const userRoles = JSON.parse(localStorage.getItem("roles"));
	const isAdmin = validRoles(userRoles, ["administrador"]);

	const { mutate: approve, isLoading: loadApprove } = useAppOrderApprove(base);
	const { mutate: reject, isLoading: loadReject } = useAppOrderReject(base);
	const { mutate: finish, isLoading: loadFinish } = useAppOrderFinish(base);
	const { mutate: duplicate, isLoading: loadDuplicate } = useAppOrderDuplicate(base);
	const { mutate: returnState, isLoading: loadReturn } = useAppOrderReturnState(base);
	const { mutate: checkStatus, isLoading: loadCheckStatus } = useAppOrderCheckStatus(base);

	const { data: response } = useApplicationOrdersPaginate(base);
	const { permissions } = response || {};

	const { mutate: remove, isLoading: loadDelete } = useAppOrderDelete(base);

	const { mutate: downloadPDF, isLoading: isDownloadigPDF } = useDownloadPdf(item.code);

	const navigate = useNavigate();

	const [showApprove, setShowApprove] = useState(false);
	const [showFinish, setShowFinish] = useState(false);
	const [showReject, setShowReject] = useState(false);
	const [showDelete, setShowDelete] = useState(false);
	const [showDuplicate, setShowDuplicate] = useState(false);
	const [showReturn, setShowReturn] = useState(false);

	const handleDownloadPDF = useCallback(() => {
		downloadPDF(
			{
				code: item.app_order_id,
			},
			{
				onSuccess: () => {
					addToast(`PDF OA ${item.code} descargado correctamente.`, {
						appearance: "success",
					});
				},
			},
		);
	}, [downloadPDF, item.app_order_id, item.code, addToast]);

	const handleFinish = () => {
		setShowFinish(false);

		const request = {
			base,
			element: item,
		};

		finish(request, {
			onSuccess: () => {
				addToast(`OA finalizada correctamente.`, {
					appearance: "success",
				});
			},
		});
	};

	const handleApprove = () => {
		setShowApprove(false);

		const request = {
			base,
			element: item,
		};

		approve(request, {
			onSuccess: () => {
				addToast(`OA aprobada correctamente.`, {
					appearance: "success",
				});
			},
		});
	};

	const handleReturn = () => {
		setShowReturn(false);

		const request = {
			base,
			element: item,
		};

		returnState(request, {
			onSuccess: () => {
				addToast(`OA devuelta correctamente.`, {
					appearance: "success",
				});
			},
		});
	};

	const handleCheckStatus = useCallback(() => {
		const request = {
			base,
			element: item,
		};

		checkStatus(request, {
			onSuccess: () => {
				addToast(`OA revisada correctamente.`, {
					appearance: "success",
				});
			},
		});
	}, [base, item, checkStatus, addToast]);

	const handleReject = () => {
		setShowReject(false);

		const request = {
			base,
			element: item,
		};

		reject(request, {
			onSuccess: () => {
				addToast(`OA rechazada correctamente.`, {
					appearance: "success",
				});
			},
		});
	};

	const handleDelete = () => {
		setShowDelete(false);

		const request = {
			base,
			element: item,
		};

		remove(request);
	};

	const handleDuplicate = () => {
		setShowDuplicate(false);

		const request = {
			base,
			element: item,
		};

		duplicate(request);
	};

	const menuItems = useMemo(() => {
		const items = [];

		if (hasPermission("show", permissions) && !approveRejectOnly) {
			items.push({
				label: "Vista Previa",
				icon: Eye,
				action: () => navigate(`/ordenes_aplicacion?oa=${item.app_order_id}&action=vista_previa`),
			});
		}

		if (hasPermission("show", permissions) && !approveRejectOnly) {
			items.push({
				label: "Generar PDF",
				icon: FileText,
				action: handleDownloadPDF,
			});
		}

		items.push({
			label: "Ver Adjuntos",
			icon: Paperclip,
			action: () => {
				const oldCache = queryClient.getQueryData(["application_orders", { ...base }]);
				let newData = [...oldCache.list.data];
				newData[indice].show_document = !newData[indice].show_document;
				setData(newData);
			},
		});

		if (hasPermission("edit", permissions)) {
			items.push({
				label: "Crear copia",
				icon: Copy,
				action: () => setShowDuplicate(true),
			});
		}

		if (hasPermission("edit", permissions) && !approveRejectOnly) {
			items.push({
				label: "Editar",
				icon: Edit,
				action: () => navigate(`/orden_aplicacion/editar/${item.app_order_id}`),
			});
		}

		items.push({ separator: true });

		if (hasPermission("edit", permissions) && [2, 4].includes(Number(item.application_orders_state_id))) {
			items.push({
				label: "Finalizar",
				icon: CircleStop,
				action: () => setShowFinish(true),
			});
		}

		if (Number(item.application_orders_state_id) === 1 && hasPermission("approve", permissions)) {
			items.push({
				label: "Aprobar",
				icon: CheckCircle,
				action: () => setShowApprove(true),
			});
		}

		if (hasPermission("edit", permissions)) {
			items.push({
				label: "Volver un estado atrás",
				icon: Undo2,
				action: () => setShowReturn(true),
			});
		}

		if (Number(item.application_orders_state_id) === 1 && hasPermission("approve", permissions)) {
			items.push({
				label: "Rechazar",
				icon: XCircle,
				action: () => setShowReject(true),
				danger: true,
			});
		}

		if (isAdmin) {
			items.push({
				label: "Check Estado",
				icon: Search,
				action: handleCheckStatus,
			});
		}

		items.push({ separator: true });
		items.push({
			label: "Dar Aviso de Aplicación",
			icon: Bell,
			action: () => navigate(`/ordenes_aplicacion?oa=${item.id}&action=dar_aviso`),
		});

		if (hasPermission("delete", permissions) && !approveRejectOnly) {
			items.push({ separator: true });
			items.push({
				label: "Eliminar",
				icon: Trash2,
				action: () => setShowDelete(true),
				danger: true,
			});
		}

		return items;

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		permissions,
		approveRejectOnly,
		item.app_order_id,
		item.application_orders_state_id,
		isAdmin,
		history,
		handleDownloadPDF,
		handleCheckStatus,
		queryClient,
		base,
		indice,
		setData,
	]);

	const isLoading = loadApprove || loadReject || loadFinish || loadDelete || isDownloadigPDF || loadDuplicate || loadReturn || loadCheckStatus;

	if (approveRejectOnly) {
		return (
			<>
				{isLoading ? <Fetching topMargin={"0px"} /> : <div onClick={(e) => e.stopPropagation()}>{approveRejectOnlyComponent}</div>}
				{renderConfirmModals()}
			</>
		);
	}

	return (
		<>
			{isLoading ? <Fetching topMargin={"0px"} /> : <CustomDropdown title={title} items={menuItems} align="right" />}
			{renderConfirmModals()}
		</>
	);

	function renderConfirmModals() {
		return (
			<>
				<BasicConfirm
					message={"¿Está seguro de que desea volver un estado atrás la orden de aplicación seleccionada?"}
					showConfirm={showReturn}
					setShowConfirm={setShowReturn}
					handleAction={handleReturn}
				/>

				<BasicConfirm
					message={"¿Está seguro de que desea aprobar la orden de aplicación seleccionada?"}
					showConfirm={showApprove}
					setShowConfirm={setShowApprove}
					handleAction={handleApprove}
				/>

				<BasicConfirm
					message={"¿Está seguro de que desea rechazar la orden de aplicación seleccionada?"}
					showConfirm={showReject}
					setShowConfirm={setShowReject}
					handleAction={handleReject}
				/>

				<BasicConfirm
					message={"¿Está seguro de que desea eliminar la orden de aplicación seleccionada?"}
					showConfirm={showDelete}
					setShowConfirm={setShowDelete}
					handleAction={handleDelete}
				/>

				<BasicConfirm
					message={"¿Está seguro de que desea finalizar la orden de aplicación seleccionada?"}
					showConfirm={showFinish}
					setShowConfirm={setShowFinish}
					handleAction={handleFinish}
				/>

				<BasicConfirm
					message={`¿Deseas duplicar la orden ${item.code}?`}
					showConfirm={showDuplicate}
					setShowConfirm={setShowDuplicate}
					handleAction={handleDuplicate}
				/>

				<Modal
					show={Boolean(item.show_document)}
					onHide={() => {
						const oldCache = queryClient.getQueryData(["application_orders", { ...base }]);
						let newData = [...oldCache.list.data];
						newData[indice].show_document = false;
						setData(newData);
					}}
					backdrop="static"
					keyboard={false}
				>
					<Modal.Header closeButton>
						<Modal.Title style={{ fontSize: "1em" }}>Documentos</Modal.Title>
					</Modal.Header>
					<Modal.Body>
						{item.documents &&
							item.documents.map((file, j) => {
								return (
									<div key={j} className={classes.document_modal}>
										<div className={classes.document_number}>{j + 1}.</div>
										<div
											className={classes.document_name}
											onClick={() => {
												const link = document.createElement("a");
												link.href = file.url;
												link.download = file.name;
												if (["pdf", "png", "jpeg", "jpg"].includes(file.extension)) {
													link.target = "_blank";
												}
												link.click();
											}}
										>
											{file.name}
										</div>
									</div>
								);
							})}
					</Modal.Body>
				</Modal>
			</>
		);
	}
};

export default AppOrdersActionItem;
